/**
 * Surface content — the field-mapping table for the Migration page's ONE
 * badged concept section (PX-W7.5, TASK-501F9B15).
 *
 * SURVIVES per Agon's 2026-09-18 ruling: the Migration page became a
 * `section` HYBRID rather than flipping fully live, because the fixture's
 * column-level field-mapping table (`MAPPING_TABLE`, `mappingRows`,
 * `PROPOSED_FIELDS`) has no backend model and no route (nearest real object
 * is `MigrationScopeEntry` on the plan — scope, not column-level mapping).
 * Pruned to exactly the symbols `FieldMappingConceptSection.tsx` renders —
 * every other symbol this file used to export (the rehearsal log, the page
 * chrome, the KPI row, the anchors, the density note) moved to the six live
 * panels or was retired outright once `client/src/features/migration/`
 * replaced them.
 *
 * The table is rendered STATICALLY (no act, per the ruling): `mappingRows`
 * always renders the PROPOSED state, since the fixture's single
 * split-decision button does not survive and there is no `demoSlice` left
 * to read an acceptance from.
 */

import { personById, agentById, thinSpace } from '@/demo/data'

const ANOUK = personById('anouk-de-vries')
const GENERATOR = agentById('mapping-generator')

/* ── Mapping table ──────────────────────────────────────────────────────── */

export type MappingStatus = 'Mapped' | 'Needs a person' | 'Blocked · client'

export interface MappingRow {
  key: string
  /** Legacy column name as it appears in the export — a code, set in mono. */
  legacyField: string
  /** Odoo model, or null when nothing is targeted yet. */
  targetModel: string | null
  /** Odoo field on that model. */
  targetField: string | null
  /** Prose target used when a decision replaced the model/field pair. */
  targetNote: string | null
  rule: string
  /** The generator's self-assessment; null once a person decided, or when
   *  there is nothing to assess. */
  confidencePct: number | null
  status: MappingStatus
  /** Set on a row a named human has resolved. */
  decidedBy: string | null
}

const ART_NR: MappingRow = {
  key: 'ART_NR',
  legacyField: 'ART_NR',
  targetModel: 'product.template',
  targetField: 'default_code',
  targetNote: null,
  rule: 'Direct',
  confidencePct: 99,
  status: 'Mapped',
  decidedBy: null,
}

const PRIJS_BRUTO: MappingRow = {
  key: 'PRIJS_BRUTO',
  legacyField: 'PRIJS_BRUTO',
  targetModel: 'product.pricelist.item',
  targetField: 'fixed_price',
  targetNote: null,
  rule: 'Divide by 100',
  confidencePct: 97,
  status: 'Mapped',
  decidedBy: null,
}

const ACTIE_CODE_PROPOSED: MappingRow = {
  key: 'ACTIE_CODE',
  legacyField: 'ACTIE_CODE',
  targetModel: null,
  targetField: null,
  targetNote: null,
  rule: 'Three meanings in the source data',
  confidencePct: 41,
  status: 'Needs a person',
  decidedBy: null,
}

/** The same object after the split is accepted — a decision, not a guess,
 *  so the confidence figure retires and a named human takes its place. Kept
 *  for the type's completeness; the badged section never selects this
 *  branch (no act survives to reach it). */
const ACTIE_CODE_DECIDED: MappingRow = {
  ...ACTIE_CODE_PROPOSED,
  targetNote: 'split into 3 fields',
  rule: 'Rule per record type',
  confidencePct: null,
  status: 'Mapped',
  decidedBy: ANOUK.name.split(' ')[0] ?? ANOUK.name,
}

const WINKEL_GRP: MappingRow = {
  key: 'WINKEL_GRP',
  legacyField: 'WINKEL_GRP',
  targetModel: 'res.partner',
  targetField: 'store_group_id',
  targetNote: null,
  rule: 'Lookup table, 6 unmatched',
  confidencePct: 88,
  status: 'Needs a person',
  decidedBy: null,
}

const LOYAL_PTS: MappingRow = {
  key: 'LOYAL_PTS',
  legacyField: 'LOYAL_PTS',
  targetModel: null,
  targetField: null,
  targetNote: null,
  rule: 'Source export missing 2 columns',
  confidencePct: null,
  status: 'Blocked · client',
  decidedBy: null,
}

/**
 * Needs-attention first, blocked strongest — the order the table's own
 * caption promises. `splitAccepted` is always called with `false` from the
 * badged section (Agon's ruling: the act does not survive), so
 * `ACTIE_CODE_DECIDED` is unreachable in production but kept so the
 * function's shape still documents both branches.
 */
export const mappingRows = (splitAccepted: boolean): readonly MappingRow[] => [
  LOYAL_PTS,
  splitAccepted ? ACTIE_CODE_DECIDED : ACTIE_CODE_PROPOSED,
  WINKEL_GRP,
  ART_NR,
  PRIJS_BRUTO,
]

export const MAPPING_TABLE = {
  label: 'Field map',
  heading: 'Objects and their field map',
  caption: 'The mapping generator proposes; a named person merges.',
  columns: {
    legacyField: 'Legacy field',
    odooTarget: 'Odoo target',
    rule: 'Rule',
    confidence: 'Confidence',
    status: 'Status',
  },
} as const

/* ── The ambiguous column: proposal and its record ───────────────────────── */

const SPLIT_ROWS = 1842
const SPLIT_ROWS_LABEL = thinSpace(SPLIT_ROWS)

export interface ProposedField {
  key: string
  model: string
  field: string
  meaning: string
  when: string
}

export const SPLIT = {
  field: 'ACTIE_CODE' as const,
  rows: SPLIT_ROWS,
  rowsLabel: SPLIT_ROWS_LABEL,
  by: ANOUK.name,
  byRole: `${ANOUK.role} · ${ANOUK.office}`,
  atLabel: '09:30',

  panelLabel: 'Ambiguous · ACTIE_CODE',
  proposalTag: 'Proposed by PX',
  proposedByAgent: GENERATOR.name,
  agentRuns: GENERATOR.runs,
  agentGate: GENERATOR.reviewGate,
  prose:
    'The generator found three uses of one column: a promotion code, a supplier action, and a free-text note. ' +
    'It has proposed a split into three fields with a rule per record type, and needs a decision on records ' +
    `where the pattern is unclear (${SPLIT_ROWS_LABEL} rows).`,
} as const

export const PROPOSED_FIELDS: readonly ProposedField[] = [
  {
    key: 'promo',
    model: 'product.pricelist.item',
    field: 'x_promo_code',
    meaning: 'Promotion code',
    when: 'Record carries a price rule for the same period',
  },
  {
    key: 'supplier',
    model: 'res.partner',
    field: 'x_supplier_action',
    meaning: 'Supplier action',
    when: 'Record is a supplier and the code matches an agreed action list',
  },
  {
    key: 'note',
    model: 'product.template',
    field: 'x_actie_note',
    meaning: 'Free-text note',
    when: 'Neither pattern matches — the value is carried across as text',
  },
]

export const SPLIT_RECORD = {
  label: 'Record',
  heading: 'ACTIE_CODE — the proposal and its decision',
  proposalLine: (agent: string, runs: string, gate: string) =>
    `Proposed by ${agent} · ${runs} · ${gate}.`,
  fieldsHeading: 'The three proposed fields',
  decisionHeading: 'Decision',
  pendingLine: `Pending — no person has merged this yet. ${SPLIT_ROWS_LABEL} rows sit behind the decision.`,
  columns: { meaning: 'Meaning', target: 'Odoo target', when: 'Applies when' },
} as const
